import React, { useState, useEffect, useRef } from "react"
import dayjs from "dayjs"
import {
  addYears,
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
  sub,
} from "date-fns"
import { Button } from "../../../../components/ui/button"
import { Calendar } from "react-date-range"
import { SvgCalendar } from "../../../../components/icons"

interface Props {
  onRangeChange: (start: string, end: string) => void
}

const CustomDateRange: React.FC<Props> = ({ onRangeChange }) => {
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [selectingStart, setSelectingStart] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    if (startDate && endDate) {
      onRangeChange(
        dayjs(startDate).format("YYYY-MM-DD"),
        dayjs(endDate).format("YYYY-MM-DD")
      )
    }
  }, [startDate, endDate])

  const handleDayClick = (day: Date) => {
    if (selectingStart) {
      setStartDate(day)
      setEndDate(null)
      setSelectingStart(false)
    } else {
      if (startDate && day < startDate) {
        setStartDate(day)
      } else {
        setEndDate(day)
      }
      setSelectingStart(true)
      setIsOpen(false)
    }
  }

  const presets = [
    {
      label: "Last 30 Days",
      range: {
        startDate: sub(new Date(), { days: 30 }),
        endDate: new Date(),
      },
    },
    {
      label: "This Month",
      range: {
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date()),
      },
    },
    {
      label: "Last Month",
      range: {
        startDate: startOfMonth(sub(new Date(), { months: 1 })),
        endDate: endOfMonth(sub(new Date(), { months: 1 })),
      },
    },
    {
      label: "This Year",
      range: {
        startDate: startOfYear(new Date()),
        endDate: endOfYear(new Date()),
      },
    },
    {
      label: "Last Year",
      range: {
        startDate: startOfYear(addYears(new Date(), -1)),
        endDate: endOfYear(addYears(new Date(), -1)),
      },
    },
  ]

  return (
    <div className="relative">
      <Button
        variant="secondary"
        onClick={() => setIsOpen((o) => !o)}
        className="h-[56px] flex items-center justify-center gap-2 w-full sm:w-[220px] md:w-[250px] lg:w-[280px] max-w-full truncate"
      >
        <SvgCalendar />
        <span className="text-center">
          {startDate && endDate
            ? `${format(startDate, "MMM d, yyyy")} - ${format(
                endDate,
                "MMM d, yyyy"
              )}`
            : "Sort by date..."}
        </span>
      </Button>

      {isOpen && (
        <>
          <style>{`
      @media (min-width: 420px) and (max-width: 639px) {
        .calendar-popup {
          left: 66.6667% !important;
        }
      }
    `}</style>

          <div
            ref={popupRef}
            className="absolute top-[64px] left-1/2 sm:left-3/4 -translate-x-1/2 bg-white border rounded-xl shadow-lg p-4 z-50 w-[360px] calendar-popup"
          >
            <style>{`
        .rdrDay { aspect-ratio: 1 / 1 !important; }
        .rdrDayNumber span {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }
      `}</style>
            <Calendar
              date={
                selectingStart ? startDate || new Date() : endDate || new Date()
              }
              onChange={(day: Date) => handleDayClick(day)}
              color="#4750F5"
              dayContentRenderer={(day: Date) => {
                const isInRange =
                  startDate &&
                  endDate &&
                  day >= startDate &&
                  day <= endDate &&
                  startDate !== endDate
                const isStart = startDate && dayjs(day).isSame(startDate, "day")
                const isEnd = endDate && dayjs(day).isSame(endDate, "day")

                return (
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full ${
                      isStart || isEnd
                        ? "bg-[#4750F5] text-white font-semibold"
                        : isInRange
                        ? "bg-[#E6E8FF]"
                        : ""
                    }`}
                  >
                    {day.getDate()}
                  </div>
                )
              }}
            />
            <div className="grid grid-cols-2 gap-2 mt-4">
              {presets.map((p) => (
                <Button
                  key={p.label}
                  variant="secondary"
                  onClick={() => {
                    setStartDate(p.range.startDate)
                    setEndDate(p.range.endDate)
                    setIsOpen(false)
                  }}
                  className="text-[14px]"
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default CustomDateRange
